import { useContext, useEffect, useState } from "react";
import {
    Alert, Box, Button, Card, CircularProgress, Divider, FormControl, FormLabel,
    Grid, Input, Stack, Typography, Chip,
} from "@mui/joy";
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';

import { usePartner } from "../../PartnerContext";
import {
    APIGetPartnerProfile,
    APIUpdatePartnerProfile,
    APIUpdatePartnerBankDetails,
} from "../../api_partner";


function formatCardInput(value) {
    const digits = value.replace(/\D/g, '').slice(0, 19);
    return digits.replace(/(.{4})/g, '$1 ').trim();
}


export default function PartnerProfileForm() {
    const { partnerToken: token } = usePartner();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    // Профиль партнёра
    const [profileForm, setProfileForm] = useState({ full_name: '', email: '', phone: '' });
    const [savingProfile, setSavingProfile] = useState(false);
    const [profileMsg, setProfileMsg] = useState(null);

    // Реквизиты
    const [bankForm, setBankForm] = useState({
        entity_type: 'individual',   // 'individual' | 'company'
        // физлицо
        card_holder_name: '',
        card_number: '',
        // юрлицо / ИП
        legal_name: '',
        account_number: '',
        bik: '',
        corr_account: '',
        kpp: '',
        // общее
        bank_name: '',
        inn: '',
    });
    const [savingBank, setSavingBank] = useState(false);
    const [bankMsg, setBankMsg] = useState(null);

    const load = async () => {
        try {
            setLoading(true);
            const res = await APIGetPartnerProfile(token);
            setData(res);

            const p = res?.partner;
            if (p) {
                setProfileForm({
                    full_name: p.full_name || '',
                    email: p.email || '',
                    phone: p.phone || '',
                });
            }
            const b = res?.bank_details;
            if (b) {
                setBankForm(f => ({
                    ...f,
                    entity_type: b.entity_type || 'individual',
                    card_holder_name: b.card_holder_name || '',
                    legal_name: b.legal_name || '',
                    bik: b.bik || '',
                    corr_account: b.corr_account || '',
                    kpp: b.kpp || '',
                    bank_name: b.bank_name || '',
                    inn: b.inn || '',
                }));
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (token) load(); }, [token]);

    const saveProfile = async () => {
        setProfileMsg(null);
        try {
            setSavingProfile(true);
            // email теперь меняется отдельным флоу — здесь не отправляем
            await APIUpdatePartnerProfile(token, {
                full_name: profileForm.full_name,
                phone: profileForm.phone,
            });
            setProfileMsg({ type: 'success', text: 'Контактные данные обновлены' });
        } catch (e) {
            setProfileMsg({ type: 'danger', text: e?.response?.data?.error || 'Ошибка сохранения' });
        } finally {
            setSavingProfile(false);
        }
    };

    const saveBank = async () => {
        setBankMsg(null);
        try {
            setSavingBank(true);
            const payload = {
                ...bankForm,
                card_number: (bankForm.card_number || '').replace(/\s/g, ''),
                account_number: (bankForm.account_number || '').replace(/\s/g, ''),
            };
            await APIUpdatePartnerBankDetails(token, payload);
            setBankMsg({ type: 'success', text: 'Реквизиты обновлены' });
            await load(); // перезагружаем, чтобы получить маску
            setBankForm(f => ({ ...f, card_number: '', account_number: '' }));
        } catch (e) {
            const d = e?.response?.data || {};
            const firstErr = d.card_number?.[0] || d.account_number?.[0] || d.bik?.[0]
                || d.inn?.[0] || d.legal_name?.[0] || d.error || 'Ошибка сохранения';
            setBankMsg({ type: 'danger', text: firstErr });
        } finally {
            setSavingBank(false);
        }
    };

    if (loading) {
        return <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>;
    }

    const partner = data?.partner;

    return (
        <Stack spacing={2}>
            {/* Системная информация (нередактируемая) */}
            <Card variant="soft" color="primary" sx={{ p: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <Box>
                        <Typography level="body-xs">Зона ответственности</Typography>
                        <Typography level="title-md">
                            {partner?.scope_type === 'region'
                                ? `Регион: ${partner?.scope_region}`
                                : `Город: ${partner?.scope_city}`}
                        </Typography>
                    </Box>
                    <Divider orientation="vertical" />
                    <Box>
                        <Typography level="body-xs">Комиссия</Typography>
                        <Typography level="title-md">{partner?.commission_percent}%</Typography>
                    </Box>
                    <Divider orientation="vertical" />
                    <Chip color={partner?.is_active ? 'success' : 'danger'} variant="solid" size="sm">
                        {partner?.is_active ? 'Активен' : 'Не активен'}
                    </Chip>
                </Stack>
                <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                    Эти параметры устанавливает администратор. Для изменения свяжитесь с поддержкой.
                </Typography>
            </Card>

            {/* Контактные данные */}
            <Card variant="outlined" sx={{ p: 2 }}>
                <Typography level="title-md" sx={{ mb: 1.5 }}>Контактные данные</Typography>
                {profileMsg && (
                    <Alert color={profileMsg.type} sx={{ mb: 1.5 }}>{profileMsg.text}</Alert>
                )}
                <Grid container spacing={1.5}>
                    <Grid xs={12}>
                        <FormControl>
                            <FormLabel>ФИО</FormLabel>
                            <Input
                                value={profileForm.full_name}
                                onChange={e => setProfileForm(f => ({ ...f, full_name: e.target.value }))}
                            />
                        </FormControl>
                    </Grid>
                    <Grid xs={12} md={6}>
                        <FormControl>
                            <FormLabel>Email</FormLabel>
                            <Input
                                type="email"
                                value={profileForm.email}
                                readOnly
                                disabled
                            />
                            <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                                Смена email требует подтверждения. Обратитесь в поддержку.
                            </Typography>
                        </FormControl>
                    </Grid>
                    <Grid xs={12} md={6}>
                        <FormControl>
                            <FormLabel>Телефон</FormLabel>
                            <Input
                                value={profileForm.phone}
                                onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
                <Button
                    startDecorator={<SaveRoundedIcon />}
                    sx={{ mt: 2, alignSelf: 'flex-start' }}
                    onClick={saveProfile}
                    loading={savingProfile}
                >
                    Сохранить
                </Button>
            </Card>

            {/* Реквизиты карты */}
            {/* Реквизиты для выплат */}
            <Card variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <CreditCardRoundedIcon color="primary" />
                    <Typography level="title-md">Реквизиты для выплат</Typography>
                </Stack>

                {/* Тип субъекта */}
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip
                        variant={bankForm.entity_type === 'individual' ? 'solid' : 'outlined'}
                        color="primary"
                        onClick={() => setBankForm(f => ({ ...f, entity_type: 'individual' }))}
                        sx={{ cursor: 'pointer' }}
                    >
                        Физлицо (карта)
                    </Chip>
                    <Chip
                        variant={bankForm.entity_type === 'company' ? 'solid' : 'outlined'}
                        color="primary"
                        onClick={() => setBankForm(f => ({ ...f, entity_type: 'company' }))}
                        sx={{ cursor: 'pointer' }}
                    >
                        Юрлицо / ИП (счёт)
                    </Chip>
                </Stack>

                {data?.bank_details?.entity_type === 'company' && data?.bank_details?.account_masked && (
                    <Alert color="primary" variant="soft" sx={{ mb: 1.5 }}>
                        Текущий счёт: <b>{data.bank_details.account_masked}</b>
                        {data?.bank_details?.bank_name && ` • ${data.bank_details.bank_name}`}
                    </Alert>
                )}
                {data?.bank_details?.entity_type !== 'company' && data?.bank_details?.card_masked && (
                    <Alert color="primary" variant="soft" sx={{ mb: 1.5 }}>
                        Текущая карта: <b>{data.bank_details.card_masked}</b>
                        {data?.bank_details?.bank_name && ` • ${data.bank_details.bank_name}`}
                    </Alert>
                )}

                {bankMsg && <Alert color={bankMsg.type} sx={{ mb: 1.5 }}>{bankMsg.text}</Alert>}

                {bankForm.entity_type === 'individual' ? (
                    /* ---------- ФИЗЛИЦО ---------- */
                    <Grid container spacing={1.5}>
                        <Grid xs={12} md={6}>
                            <FormControl>
                                <FormLabel>Имя держателя (как на карте)</FormLabel>
                                <Input
                                    value={bankForm.card_holder_name}
                                    onChange={e => setBankForm(f => ({ ...f, card_holder_name: e.target.value }))}
                                    placeholder="IVAN IVANOV"
                                />
                            </FormControl>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <FormControl>
                                <FormLabel>
                                    Номер карты {data?.bank_details?.card_masked && '(новый)'}
                                </FormLabel>
                                <Input
                                    value={bankForm.card_number}
                                    onChange={e => setBankForm(f => ({ ...f, card_number: formatCardInput(e.target.value) }))}
                                    placeholder="0000 0000 0000 0000"
                                />
                            </FormControl>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <FormControl>
                                <FormLabel>Банк</FormLabel>
                                <Input
                                    value={bankForm.bank_name}
                                    onChange={e => setBankForm(f => ({ ...f, bank_name: e.target.value }))}
                                    placeholder="Тинькофф / Сбер / ..."
                                />
                            </FormControl>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <FormControl>
                                <FormLabel>ИНН (опционально)</FormLabel>
                                <Input
                                    value={bankForm.inn}
                                    onChange={e => setBankForm(f => ({ ...f, inn: e.target.value }))}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                ) : (
                    /* ---------- ЮРЛИЦО / ИП ---------- */
                    <Grid container spacing={1.5}>
                        <Grid xs={12} md={6}>
                            <FormControl>
                                <FormLabel>Юр. название / ИП</FormLabel>
                                <Input
                                    value={bankForm.legal_name}
                                    onChange={e => setBankForm(f => ({ ...f, legal_name: e.target.value }))}
                                    placeholder='ООО «Ромашка» / ИП Иванов И.И.'
                                />
                            </FormControl>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <FormControl>
                                <FormLabel>Расчётный счёт (20 цифр) {data?.bank_details?.account_masked && '(новый)'}</FormLabel>
                                <Input
                                    value={bankForm.account_number}
                                    onChange={e => setBankForm(f => ({ ...f, account_number: e.target.value.replace(/\D/g, '').slice(0, 20) }))}
                                    placeholder="40702810..."
                                />
                            </FormControl>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <FormControl>
                                <FormLabel>БИК (9 цифр)</FormLabel>
                                <Input
                                    value={bankForm.bik}
                                    onChange={e => setBankForm(f => ({ ...f, bik: e.target.value.replace(/\D/g, '').slice(0, 9) }))}
                                    placeholder="044525..."
                                />
                            </FormControl>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <FormControl>
                                <FormLabel>ИНН</FormLabel>
                                <Input
                                    value={bankForm.inn}
                                    onChange={e => setBankForm(f => ({ ...f, inn: e.target.value.replace(/\D/g, '').slice(0, 12) }))}
                                />
                            </FormControl>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <FormControl>
                                <FormLabel>КПП (опционально)</FormLabel>
                                <Input
                                    value={bankForm.kpp}
                                    onChange={e => setBankForm(f => ({ ...f, kpp: e.target.value.replace(/\D/g, '').slice(0, 9) }))}
                                />
                            </FormControl>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <FormControl>
                                <FormLabel>Банк</FormLabel>
                                <Input
                                    value={bankForm.bank_name}
                                    onChange={e => setBankForm(f => ({ ...f, bank_name: e.target.value }))}
                                    placeholder="Наименование банка"
                                />
                            </FormControl>
                        </Grid>
                        <Grid xs={12} md={6}>
                            <FormControl>
                                <FormLabel>Корр. счёт (опционально)</FormLabel>
                                <Input
                                    value={bankForm.corr_account}
                                    onChange={e => setBankForm(f => ({ ...f, corr_account: e.target.value.replace(/\D/g, '').slice(0, 20) }))}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                )}

                <Typography level="body-xs" color="neutral" sx={{ mt: 1.5 }}>
                    Полные реквизиты не отображаются в кабинете и хранятся в зашифрованном виде.
                    Вы видите только последние 4 цифры.
                </Typography>

                <Button
                    startDecorator={<SaveRoundedIcon />}
                    sx={{ mt: 2, alignSelf: 'flex-start' }}
                    onClick={saveBank}
                    loading={savingBank}
                    disabled={
                        bankForm.entity_type === 'individual'
                            ? (!bankForm.card_number || !bankForm.card_holder_name)
                            : (!bankForm.account_number || !bankForm.bik || !bankForm.inn || !bankForm.legal_name)
                    }
                >
                    Сохранить реквизиты
                </Button>
            </Card>
        </Stack>
    );
}
